import {useStore} from 'react-redux';
import {type Store} from 'redux';

import {type RootState} from '../../types/redux';
import type {setHighlight, setHighlights} from './actions';
import {type Highlight} from './types';

/** Reads the cross-panel highlight and reports changes without React state. */
export type HighlightSource = {
  get: () => Highlight;
  subscribe: (onChange: () => void) => () => void;
};

/** Writes one highlight axis (useSetHighlight). */
export type SetHighlight = (...args: Parameters<typeof setHighlight>) => void;

/** Writes several highlight axes at once (useSetHighlights). */
export type SetHighlights = (...args: Parameters<typeof setHighlights>) => void;

const sourcesByStore = new WeakMap<Store<RootState>, HighlightSource>();

function createHighlightSource(store: Store<RootState>): HighlightSource {
  return {
    get: function getHighlight() {
      return store.getState().interactState.highlight;
    },
    subscribe: function subscribeToHighlight(onChange) {
      let previous = store.getState().interactState.highlight;
      return store.subscribe(function onStoreChange() {
        const next = store.getState().interactState.highlight;
        if (next !== previous) {
          previous = next;
          onChange();
        }
      });
    },
  };
}

/**
 * The highlight source for a store. Each store gets one source, so its
 * identity never changes and it can be passed down without memoization.
 */
export function getHighlightSource(store: Store<RootState>): HighlightSource {
  const existing = sourcesByStore.get(store);
  if (existing != null) {
    return existing;
  }
  const source = createHighlightSource(store);
  sourcesByStore.set(store, source);
  return source;
}

/**
 * The highlight as a stable source. Pass it down instead of highlight values,
 * so a crosshair move re-renders only the components that read that axis.
 */
export function useHighlightSource(): HighlightSource {
  return getHighlightSource(useStore<RootState>());
}
